import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save } from 'lucide-react';
import Layout from '../../components/Layout';
import departmentService, { UpdateDepartmentDTO } from '../../services/departmentService';
import '../../styles/ImsTheme.css';
import './DepartmentEditPage.css';

import { getServerErrorMessage } from '../../utils/errorUtils';
interface FieldErrors {
  departmentName?: string;
  departmentCode?: string;
  departmentShortName?: string;
}

const DepartmentEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [department, setDepartment] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<UpdateDepartmentDTO>({
    departmentName: '',
    departmentCode: '',
    departmentShortName: '',
    departmentType: 'Sales',
    description: '',
    isActive: true,
  });

  const departmentTypes = ['Sales', 'Finance', 'Warehouse', 'HR', 'Operations', 'IT'];
  const MAX_LENGTHS = {
    departmentName: 30,
    departmentCode: 10,
    departmentShortName: 3,
    description: 200,
  };

  useEffect(() => {
    loadDepartment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDepartment = async () => {
    setLoading(true);
    setError(null);
    try {
      const deptData = await departmentService.getDepartmentById(parseInt(id || '0'));
      setDepartment(deptData);
      setFormData({
        departmentName: deptData.departmentName,
        departmentCode: deptData.departmentCode,
        departmentShortName: deptData.departmentShortName || '',
        departmentType: deptData.departmentType || 'Sales',
        description: deptData.description || '',
        isActive: deptData.isActive,
      });
      setLoading(false);
    } catch (err) {
      setError(t('department.failedToLoadDepartment'));
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    const checkboxTarget = e.target as HTMLInputElement;

    let finalValue: any;
    if (isCheckbox) {
      finalValue = checkboxTarget.checked;
    } else {
      // Enforce max length
      finalValue = value;
      if (name === 'departmentName' && value.length > MAX_LENGTHS.departmentName) {
        finalValue = value.substring(0, MAX_LENGTHS.departmentName);
      } else if (name === 'departmentCode' && value.length > MAX_LENGTHS.departmentCode) {
        finalValue = value.substring(0, MAX_LENGTHS.departmentCode);
      } else if (name === 'departmentShortName' && value.length > MAX_LENGTHS.departmentShortName) {
        finalValue = value.substring(0, MAX_LENGTHS.departmentShortName);
      } else if (name === 'description' && value.length > MAX_LENGTHS.description) {
        finalValue = value.substring(0, MAX_LENGTHS.description);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Clear field error when user starts typing
    if (!isCheckbox && fieldErrors[name as keyof FieldErrors]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.departmentName.trim()) {
      errors.departmentName = 'Department name is required';
      isValid = false;
    }
    if (!formData.departmentCode.trim()) {
      errors.departmentCode = 'Department code is required';
      isValid = false;
    }
    if (!formData.departmentShortName.trim()) {
      errors.departmentShortName = 'Short name is required';
      isValid = false;
    }

    setFieldErrors(errors);

    // Display error message in error div if validation fails
    if (!isValid) {
      setError('Please correct the errors below before saving.');
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm() || !department) {
      return;
    }

    setSaving(true);
    try {
      await departmentService.updateDepartment(department.departmentId, formData);
      navigate('/organization/departments', {
        state: { message: 'Department updated successfully!' },
      });
    } catch (err: any) {
      const errorMessage = getServerErrorMessage(err) || t('department.failedToLoadDepartment');
      setError(errorMessage);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="edit-spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!department) {
    return (
      <Layout>
        <div className="edit-container">
          <div className="alert alert-danger">{t('department.failedToLoadDepartment')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/organization/departments')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {t('department.editTitle')}
              <span className="ims-page-subtitle">{department.departmentName}</span>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/organization/departments')} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.saveChanges')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
             <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {/* Field Validation Errors */}
        {Object.keys(fieldErrors).length > 0 && (
          <div className="alert alert-danger">
            <ul style={{ margin: '0', paddingLeft: '20px' }}>
              {Object.entries(fieldErrors).map(
                ([field, message]) =>
                  message && (
                    <li key={field} style={{ marginBottom: '4px' }}>
                      {message}
                    </li>
                  )
              )}
            </ul>
          </div>
        )}

        <div className="edit-content">
          {/* Form Card */}
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('department.details')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">
                      {t('department.departmentName')} <span className="required">*</span>
                      <span style={{ float: 'right', fontSize: '12px', color: '#9ca3af' }}>
                        {formData.departmentName.length}/{MAX_LENGTHS.departmentName}
                      </span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.departmentName ? 'is-invalid' : ''}`}
                      name="departmentName"
                      value={formData.departmentName}
                      onChange={handleInputChange}
                      placeholder={t('department.departmentName')}
                      maxLength={MAX_LENGTHS.departmentName}
                    />
                    {fieldErrors.departmentName && (
                      <small className="form-error">{fieldErrors.departmentName}</small>
                    )}
                    <small className="form-helper">e.g., Sales, Finance, HR</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('department.code')} <span className="required">*</span>
                      <span style={{ float: 'right', fontSize: '12px', color: '#9ca3af' }}>
                        {formData.departmentCode.length}/{MAX_LENGTHS.departmentCode}
                      </span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.departmentCode ? 'is-invalid' : ''}`}
                      name="departmentCode"
                      value={formData.departmentCode}
                      onChange={handleInputChange}
                      placeholder={t('department.code')}
                      maxLength={MAX_LENGTHS.departmentCode}
                    />
                    {fieldErrors.departmentCode && (
                      <small className="form-error">{fieldErrors.departmentCode}</small>
                    )}
                    <small className="form-helper">Unique code to identify the department</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('department.shortName')} <span className="required">*</span>
                      <span style={{ float: 'right', fontSize: '12px', color: '#9ca3af' }}>
                        {formData.departmentShortName.length}/{MAX_LENGTHS.departmentShortName}
                      </span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.departmentShortName ? 'is-invalid' : ''}`}
                      name="departmentShortName"
                      value={formData.departmentShortName}
                      onChange={handleInputChange}
                      placeholder="e.g., SAL, FIN, HR"
                      maxLength={MAX_LENGTHS.departmentShortName}
                    />
                    {fieldErrors.departmentShortName && (
                      <small className="form-error">{fieldErrors.departmentShortName}</small>
                    )}
                    <small className="form-helper">Short abbreviation for the department</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('department.type')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="departmentType"
                      value={formData.departmentType}
                      onChange={handleInputChange}
                    >
                      {departmentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <small className="form-helper">Select the department category</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('common.description')}
                      <span style={{ float: 'right', fontSize: '12px', color: '#9ca3af' }}>
                        {formData.description.length}/{MAX_LENGTHS.description}
                      </span>
                    </label>
                    <textarea
                      className="form-textarea"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={t('common.description')}
                      maxLength={MAX_LENGTHS.description}
                      rows={4}
                    />
                    <small className="form-helper">Provide a brief description of this department</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        style={{ marginRight: '8px' }}
                      />
                      {t('common.active')}
                    </label>
                  </div>

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="edit-sidebar">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.info')}</h3>
              </div>
              <div className="edit-card-body">
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                  <p>
                    <strong>{t('common.status')}:</strong> {formData.isActive ? t('common.active') : t('common.inactive')}
                  </p>
                  <p>
                    <strong>ID:</strong> {department.departmentId}
                  </p>
                  <p>
                    <strong>{t('common.createdDate')}:</strong>{' '}
                    {new Date(department.createdDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DepartmentEditPage;
